import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Star, Clock, HelpCircle, Share2, User, Compass, PenSquare, MoreHorizontal, LogIn, LogOut, Info, ChevronUp } from 'lucide-react';
import axios from 'axios';
import ExploreDetail from './portals/ExploreDetail';
import { isAuthenticated, clearAuth, getUser } from '../utils/auth';
import { liuyaoApi } from '../services/api_liuyao';
import { qimenApi } from '../services/api_qimen';
import { baziApi } from '../services/api_bazi';
import { apiService } from '../services/api';

/**
 * 六爻探索页面 - 瀑布流展示发布的卦例
 * 模仿小红书风格
 */
const PortalIndex = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all'); // all, qiuzhu, share
  const [category, setCategory] = useState('all'); // all, liuyao, qimen, bazi, fengshui, zeri
  const [selectedItem, setSelectedItem] = useState(null); // 当前选中的卡片
  const [showMoreMenu, setShowMoreMenu] = useState(false); // 更多菜单
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [appendAnimRange, setAppendAnimRange] = useState({ start: null, count: 0, page: 1 });

  // 检查登录状态
  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
    setCurrentUser(getUser());
  }, []);

  // 处理退出登录
  const handleLogout = () => {
    if (window.confirm('确定要退出登录吗？')) {
      clearAuth();
      setIsLoggedIn(false);
      setCurrentUser(null);
      setShowMoreMenu(false);
    }
  };

  // 获取数据
  // 支持多业务类型广场聚合查询
  const fetchData = useCallback(async (pageNum = 1, append = false) => {
    if (loading) return;
    setLoading(true);
    try {
      let allResults = [];
      let hasNext = false;
      const perPage = 20;
      if (category === 'all') {
        // 聚合全部类型
        const queryTypes = ['liuyao', 'qimen', 'bazi'];
        const requests = queryTypes.map(type => {
          const params = new URLSearchParams({
            page: pageNum,
            per_page: perPage,
            category: type,
          });
          if (filter !== 'all') params.append('type', filter);
          let url = `/api/${type}/explore?${params}`;
         
          return axios.get(url).then(res => ({ type, res })).catch(e => ({ type, res: null }));
        });
        const results = await Promise.all(requests);
        results.forEach(({ type, res }) => {
          if (res && res.data && res.data.success) {
            const arr = res.data.data || [];
            allResults = allResults.concat(arr.map(i => ({ ...i, _bizType: type })));
            if (res.data.pagination?.has_next) hasNext = true;
          }
        });
      } else {
        // 只查选中类型
        const params = new URLSearchParams({
          page: pageNum,
          per_page: perPage,
          category: category,
        });
        if (filter !== 'all') params.append('type', filter);
        let url = `/api/${category}/explore?${params}`;
       
        const res = await axios.get(url).catch(() => null);
        if (res && res.data && res.data.success) {
          const arr = res.data.data || [];
          allResults = arr.map(i => ({ ...i, _bizType: category }));
          if (res.data.pagination?.has_next) hasNext = true;
        }
      }
      // 按发布时间倒序
      allResults.sort((a, b) => new Date(b.publish_time || b.created_at || 0) - new Date(a.publish_time || a.created_at || 0));
      if (append) {
        setItems(prev => {
          setAppendAnimRange({ start: prev.length, count: allResults.length, page: pageNum });
          return [...prev, ...allResults];
        });
      } else {
        setItems(allResults);
        setAppendAnimRange({ start: null, count: 0, page: 1 });
      }
      setHasMore(hasNext);
      setPage(pageNum);
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, filter, category]);

  // 初始加载和筛选变化时重新加载
  useEffect(() => {
    setPage(1);
    setAppendAnimRange({ start: null, count: 0, page: 1 });
    fetchData(1, false);
  }, [filter, category]);

  // 加载更多
  const loadMore = () => {
    if (!loading && hasMore) {
      fetchData(page + 1, true);
    }
  };

  // 滚动加载
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 200
      ) {
        loadMore();
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [page, loading, hasMore]);

  // 处理点赞状态更新
  const handleLikeToggle = useCallback((itemId, likeData) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              is_liked: likeData.is_liked,
              likes_count: likeData.likes_count
            }
          : item
      )
    );
  }, []);

  // 处理收藏状态更新
  const handleFavoriteToggle = useCallback((itemId, favoriteData) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              is_favorited: favoriteData.is_favorited,
              favorites_count: favoriteData.favorites_count
            }
          : item
      )
    );
  }, []);

  // 处理详情页点赞状态更新
  const handleDetailLikeUpdate = useCallback((itemId, likeData) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              is_liked: likeData.is_liked,
              likes_count: likeData.likes_count
            }
          : item
      )
    );
  }, []);

  // 处理详情页收藏状态更新
  const handleDetailFavoriteUpdate = useCallback((itemId, favoriteData) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              is_favorited: favoriteData.is_favorited,
              favorites_count: favoriteData.favorites_count
            }
          : item
      )
    );
  }, []);

  // 处理详情页评论数量更新
  const handleDetailCommentUpdate = useCallback((itemId, commentCount) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? {
              ...item,
              comments_count: commentCount
            }
          : item
      )
    );
  }, []);

  // 点击卡片 - 打开弹窗，带业务类型
  const handleCardClick = (item) => {
    // 从当前items数组中查找最新的item数据，确保数据同步
    const currentItem = items.find(i => i.id === item.id);
    if (currentItem) {
      // 业务类型优先级：category > biz_type > type > 默认 liuyao
      const bizType = currentItem._bizType;
      setSelectedItem({ ...currentItem, _bizType: bizType });
    }
  };

  // 关闭弹窗
  const handleCloseDetail = () => {
    setSelectedItem(null);
  };

  // 格式化时间
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;
    
    return date.toLocaleDateString('zh-CN');
  };

  // 仅在首屏做轻量错峰入场动画，限制数量避免性能开销
  const shouldAnimateFirstScreen = page === 1 && !loading;

  return (
    <div className="min-h-screen bg-[#fafafa] md:bg-white flex flex-col md:flex-row">
      <style>{`
        @keyframes portalCardIn {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.995);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .portal-card-enter {
          opacity: 0;
          animation: portalCardIn var(--portal-enter-duration, 420ms) cubic-bezier(0.22, 1, 0.36, 1) forwards;
          will-change: transform, opacity;
        }
        .portal-card-enter-soft {
          --portal-enter-duration: 260ms;
        }
        @media (prefers-reduced-motion: reduce) {
          .portal-card-enter {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>

      {/* 底部移动端导航栏 */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white/95 backdrop-blur border-t border-gray-200 z-50 flex items-center justify-around px-2 shadow-[0_-6px_20px_rgba(0,0,0,0.06)]">
        <button 
          onClick={() => navigate('/portal')}
          className="flex flex-col items-center justify-center space-y-1 text-red-500"
        >
          <Compass className="w-6 h-6" />
          <span className="text-xs">发现</span>
        </button>
        <button
          onClick={() => navigate('/')}
          className="-mt-6 flex flex-col items-center justify-center text-white"
        >
          <span className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-lg shadow-red-200">
            <PenSquare className="w-6 h-6" />
          </span>
          <span className="text-[11px] text-gray-500 mt-0.5">发布</span>
        </button>
        {isLoggedIn ? (
          <button 
            onClick={() => navigate('/yunying/userinfo')}
            className="flex flex-col items-center justify-center space-y-1 text-gray-500 hover:text-red-500"
          >
            <User className="w-6 h-6" />
            <span className="text-xs">我的</span>
          </button>
        ) : (
          <button 
            onClick={() => navigate('/login')}
            className="flex flex-col items-center justify-center space-y-1 text-gray-500 hover:text-red-500"
          >
            <LogIn className="w-6 h-6" />
            <span className="text-xs">登录</span>
          </button>
        )}
      </div>

      {/* 左侧固定侧边栏 - PC端显示 */}
      <div className="hidden md:flex fixed left-0 top-0 h-full w-[15vw] min-w-[160px] max-w-[220px] bg-white flex-col z-20 border-r border-gray-100">
        {/* Logo */}
        <div className="p-6 flex justify-center">
          <img 
            src="/yuanyun_logo.jpg" 
            alt="缘韵" 
            className="w-[67px] h-[67px] object-contain cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          />
        </div>

        {/* 主导航 */}
        <nav className="flex-1 px-4 py-2 flex flex-col items-center">
          {/* 发现 */}
          <button
            onClick={() => navigate('/portal')}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-gray-800 font-semibold hover:bg-red-50 hover:text-red-500 transition-all group mb-2"
          >
            <Compass className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="text-lg">发现</span>
          </button>

          {/* 发布 */}
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-gray-800 font-semibold hover:bg-red-50 hover:text-red-500 transition-all group mb-2"
          >
            <PenSquare className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="text-lg">发布</span>
          </button>

          {/* 未登录时显示登录按钮 */}
          {!isLoggedIn && (
            <button
              onClick={() => navigate(`/login?return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold hover:from-red-600 hover:to-pink-600 transition-all group mt-4 shadow-md"
            >
              <LogIn className="w-6 h-6 group-hover:scale-110 transition-transform" />
              <span className="text-lg">登录</span>
            </button>
          )}

          {/* 已登录时显示用户信息 */}
          {isLoggedIn && currentUser && (
            <div 
              onClick={() => navigate('/yunying/userinfo')}
              className="mt-4 px-4 py-3 bg-gray-50 rounded-xl w-full cursor-pointer hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center justify-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm text-gray-700 truncate">{currentUser.nickname || currentUser.username}</span>
              </div>
            </div>
          )}
        </nav>

        {/* 底部更多菜单 */}
        <div className="p-4 relative">
          {showMoreMenu && (
            <div className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <button
                onClick={() => { navigate('/about'); setShowMoreMenu(false); }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Info className="w-5 h-5" />
                <span>关于我们</span>
              </button>
              {isLoggedIn && (
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 transition-colors border-t border-gray-100"
                >
                  <LogOut className="w-5 h-5" />
                  <span>退出登录</span>
                </button>
              )}
            </div>
          )}
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-100 transition-all"
          >
            {showMoreMenu ? <ChevronUp className="w-6 h-6" /> : <MoreHorizontal className="w-6 h-6" />}
            <span className="text-lg">更多</span>
          </button>
        </div>
      </div>

      {/* 主内容区域 - PC端左侧留白，移动端底部留白 */}
      <div className="flex-1 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0 md:ml-[max(15vw,160px)] transition-all ease-in-out duration-300">
      {/* 顶部导航 */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
        <div className="max-w-[92rem] mx-auto px-3 md:px-6 py-3">
          <div className="flex items-center justify-between mb-3 px-0.5">
            <div>
              <h1 className="text-base md:text-xl font-semibold text-gray-900 tracking-tight">探索广场</h1>
              <p className="hidden md:block text-sm text-gray-500 mt-0.5">实时浏览求助与分享动态</p>
            </div>

            {/* 筛选按钮：全部、求助、分享（同一行靠右） */}
            <div className="flex items-center justify-center gap-1 md:gap-2 flex-shrink-0 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-2.5 md:px-3 py-1 md:py-1.5 text-xs md:text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  filter === 'all'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setFilter('qiuzhu')}
                className={`px-2.5 md:px-3 py-1 md:py-1.5 text-xs md:text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  filter === 'qiuzhu'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                求助
              </button>
              <button
                onClick={() => setFilter('share')}
                className={`px-2.5 md:px-3 py-1 md:py-1.5 text-xs md:text-sm font-medium whitespace-nowrap rounded-full transition-all ${
                  filter === 'share'
                    ? 'bg-white text-red-500 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                分享
              </button>
            </div>
          </div>

          {/* 第二行：分类标签，左侧对齐 */}
          <div className="overflow-x-auto md:overflow-visible no-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex items-center justify-start gap-4 min-w-max md:min-w-0 md:flex-wrap pr-2">
              {[
              { key: 'all', label: '全部' },
              { key: 'bazi', label: '八字' },
              { key: 'liuyao', label: '六爻' },
              { key: 'qimen', label: '奇门' },
              { key: 'fengshui', label: '风水' },
              { key: 'zeri', label: '择日' },
              ].map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setCategory(cat.key)}
                  className={`relative text-sm font-medium px-2.5 py-1 rounded-full md:px-1 md:py-0 md:pb-1.5 md:rounded-none transition-all ${
                    category === cat.key
                      ? 'text-gray-900 bg-gray-100 md:bg-transparent'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {cat.label}
                  {category === cat.key && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-red-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 瀑布流内容 */}
      <div className="max-w-[92rem] mx-auto px-2 md:px-6 py-5 md:py-6">
        {items.length > 0 ? (
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-2 md:gap-4 space-y-2 md:space-y-4">
            {items.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className={`mb-2 md:mb-4 break-inside-avoid ${
                  (shouldAnimateFirstScreen && index < 12) ||
                  (appendAnimRange.start !== null &&
                    page === appendAnimRange.page &&
                    index >= appendAnimRange.start &&
                    index < appendAnimRange.start + appendAnimRange.count)
                    ? `portal-card-enter ${
                        shouldAnimateFirstScreen && index < 12 ? '' : 'portal-card-enter-soft'
                      }`
                    : ''
                }`}
                style={
                  shouldAnimateFirstScreen && index < 12
                    ? { animationDelay: `${index * 45}ms` }
                    : appendAnimRange.start !== null &&
                        page === appendAnimRange.page &&
                        index >= appendAnimRange.start &&
                        index < appendAnimRange.start + appendAnimRange.count
                      ? { animationDelay: `${(index - appendAnimRange.start) * 35}ms` }
                      : undefined
                }
              >
                <ExploreCard
                  item={item}
                  onClick={() => handleCardClick(item)}
                  formatTime={formatTime}
                  onLikeToggle={handleLikeToggle}
                  onFavoriteToggle={handleFavoriteToggle}
                />
              </div>
            ))}
          </div>
        ) : !loading ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔮</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">暂无内容</h3>
            <p className="text-gray-500">还没有人发布卦例，成为第一个吧！</p>
          </div>
        ) : null}

        {/* 加载更多指示器 */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-500">加载中...</span>
          </div>
        )}

        {!hasMore && items.length > 0 && (
          <div className="text-center py-8 text-gray-400">
            —— 已经到底了 ——
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {selectedItem && (
        <ExploreDetail
          item={selectedItem}
          bizType={selectedItem._bizType}
          onClose={handleCloseDetail}
          onLikeUpdate={handleDetailLikeUpdate}
          onFavoriteUpdate={handleDetailFavoriteUpdate}
          onCommentUpdate={handleDetailCommentUpdate}
        />
      )}
      </div>
    </div>
  );
};

/**
 * 探索卡片组件
 */
const ExploreCard = ({ item, onClick, formatTime, onLikeToggle, onFavoriteToggle }) => {
  const [isLiked, setIsLiked] = useState(item.is_liked || false);
  const [isFavorited, setIsFavorited] = useState(item.is_favorited || false);
  const [likesCount, setLikesCount] = useState(item.likes_count || 0);
  const [favoritesCount, setFavoritesCount] = useState(item.favorites_count || 0);
  const [isLoading, setIsLoading] = useState(false);
  
  const isQiuzhu = item.publish_type === 'qiuzhu';

  // 监听props变化，同步更新内部状态
  useEffect(() => {
    setIsLiked(item.is_liked || false);
    setIsFavorited(item.is_favorited || false);
    setLikesCount(item.likes_count || 0);
    setFavoritesCount(item.favorites_count || 0);
  }, [item.is_liked, item.likes_count, item.is_favorited, item.favorites_count]);

  // 处理点赞
  const handleLike = async (e) => {
    e.stopPropagation(); // 阻止冒泡，不触发卡片点击
    if (isLoading) return;
    
    if (!isAuthenticated()) {
      alert('请先登录');
      return;
    }
    
    setIsLoading(true);
    try {
      const bizType = item._bizType;
      const result = await apiService.toggleLike(bizType, item.id);
      
      if (result.success) {
        setIsLiked(result.data.is_liked);
        setLikesCount(result.data.likes_count);
        if (onLikeToggle) onLikeToggle(item.id, result.data);
      } else {
        alert(result.error || '操作失败');
      }
    } catch (error) {
      console.error('点赞失败:', error);
      alert('点赞失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 处理收藏
  const handleFavorite = async (e) => {
    e.stopPropagation(); // 阻止冒泡，不触发卡片点击
    if (isLoading) return;
    
    if (!isAuthenticated()) {
      alert('请先登录');
      return;
    }
    
    setIsLoading(true);
    try {
      const bizType = item._bizType;
      const result = await apiService.toggleFavorite(bizType, item.id);
      
      if (result.success) {
        setIsFavorited(result.data.is_favorited);
        setFavoritesCount(result.data.favorites_count);
        if (onFavoriteToggle) onFavoriteToggle(item.id, result.data);
      } else {
        alert(result.error || '操作失败');
      }
    } catch (error) {
      console.error('收藏失败:', error);
      alert('收藏失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 业务类型标识和颜色区分
  const typeMap = {
    liuyao: { label: '六爻', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    qimen: { label: '奇门', color: 'bg-pink-100 text-pink-700 border-pink-200' },
    bazi: { label: '八字', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    fengshui: { label: '风水', color: 'bg-teal-100 text-teal-700 border-teal-200' },
    zeri: { label: '择日', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    // 兼容后端返回的表名/类型
    BaziChartShare: { label: '八字', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    QimenChartShare: { label: '奇门', color: 'bg-pink-100 text-pink-700 border-pink-200' },
    BaziChartQiuzhu: { label: '八字', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    QimenChartQiuzhu: { label: '奇门', color: 'bg-pink-100 text-pink-700 border-pink-200' },
    LiuyaoChartShare: { label: '六爻', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    LiuyaoChartQiuzhu: { label: '六爻', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  };
  // 业务类型优先级：category > biz_type > type > model > 默认 liuyao
  let bizType = item._bizType;
  const typeInfo = typeMap[bizType] || typeMap.liuyao;

  // 卡片主色区分：求助/分享 + 业务类型
  const cardColorMap = {
    liuyao: {
      share: {
        gradient: 'from-green-500 to-emerald-400',
        badge: 'bg-green-100 text-green-700 border-green-200',
        icon: <Share2 className="w-3 h-3" />, label: '分享',
        borderColor: 'border-green-200', hoverBorder: 'hover:border-green-300', headerBg: 'bg-gradient-to-r from-green-50 to-emerald-50',
      },
      qiuzhu: {
        gradient: 'from-blue-500 to-cyan-400',
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: <HelpCircle className="w-3 h-3" />, label: '求助',
        borderColor: 'border-blue-200', hoverBorder: 'hover:border-blue-300', headerBg: 'bg-gradient-to-r from-blue-50 to-cyan-50',
      }
    },
    qimen: {
      share: {
        gradient: 'from-pink-500 to-fuchsia-400',
        badge: 'bg-pink-100 text-pink-700 border-pink-200',
        icon: <Share2 className="w-3 h-3" />, label: '分享',
        borderColor: 'border-pink-200', hoverBorder: 'hover:border-pink-300', headerBg: 'bg-gradient-to-r from-pink-50 to-fuchsia-50',
      },
      qiuzhu: {
        gradient: 'from-rose-500 to-pink-400',
        badge: 'bg-rose-100 text-rose-700 border-rose-200',
        icon: <HelpCircle className="w-3 h-3" />, label: '求助',
        borderColor: 'border-rose-200', hoverBorder: 'hover:border-rose-300', headerBg: 'bg-gradient-to-r from-rose-50 to-pink-50',
      }
    },
    bazi: {
      share: {
        gradient: 'from-orange-400 to-yellow-300',
        badge: 'bg-orange-100 text-orange-700 border-orange-200',
        icon: <Share2 className="w-3 h-3" />, label: '分享',
        borderColor: 'border-orange-200', hoverBorder: 'hover:border-orange-300', headerBg: 'bg-gradient-to-r from-orange-50 to-yellow-50',
      },
      qiuzhu: {
        gradient: 'from-amber-500 to-orange-400',
        badge: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: <HelpCircle className="w-3 h-3" />, label: '求助',
        borderColor: 'border-amber-200', hoverBorder: 'hover:border-amber-300', headerBg: 'bg-gradient-to-r from-amber-50 to-orange-50',
      }
    }
  };
  // 判断类型
  const mainType = (bizType || '').toLowerCase().includes('qimen') ? 'qimen'
    : (bizType || '').toLowerCase().includes('bazi') ? 'bazi'
    : (bizType || '').toLowerCase().includes('liuyao') ? 'liuyao'
    : bizType;
  const cardMode = isQiuzhu ? 'qiuzhu' : 'share';
  const cardColors = (cardColorMap[mainType] && cardColorMap[mainType][cardMode]) || cardColorMap.liuyao[cardMode];

  return (
    <div
      onClick={onClick}
      className={`break-inside-avoid mb-3 md:mb-4 bg-white rounded-2xl overflow-hidden shadow-sm border-2 ${cardColors.borderColor} ${cardColors.hoverBorder} hover:shadow-lg md:hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-300 cursor-pointer group`}
    >
      {/* 卡片头部 - 类型标识+业务类型 */}
      <div className={`${cardColors.headerBg} px-3 md:px-4 py-2.5 md:py-3 border-b border-gray-100`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${cardColors.badge}`}>
              {cardColors.icon}
              {cardColors.label}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${typeInfo.color}`}>
              {typeInfo.label}
            </span>
          </div>
          {item.zhanti_type && (
            <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">
              {item.zhanti_type}
            </span>
          )}
        </div>
      </div>

      {/* 卡片内容 */}
      <div className="p-3 md:p-4">
        {/* 标题/问题 */}
        <h3 className="text-gray-900 font-medium text-sm leading-relaxed mb-3 line-clamp-2 md:line-clamp-3 group-hover:text-indigo-600 transition-colors">
          {item.zhanti || item.content }
        </h3>

        {/* 悬赏/收费信息 */}
        {(isQiuzhu && item.payment_amount > 0) && (
          <div className={`flex items-center gap-1 text-sm mb-3 px-2 py-1 rounded-lg ${
            item.is_finished 
              ? 'text-gray-500 bg-gray-100' 
              : 'text-orange-500 bg-orange-50'
          }`}>
            <span className="font-semibold">
              悬赏积分 {item.payment_amount} 分
              {item.is_finished && <span className="ml-1 text-green-600 font-normal">(已结束)</span>}
            </span>
          </div>
        )}
        {(!isQiuzhu && item.charge_amount > 0) && (
          <div className="flex items-center gap-1 text-emerald-600 text-sm mb-3 bg-emerald-50 px-2 py-1 rounded-lg">
            <span className="font-semibold">付费积分 {item.charge_amount} 分</span>
          </div>
        )}

        {/* 起卦时间 */}
        {item.qiguatime && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-3">
            <Clock className="w-3 h-3" />
            <span>{item.qiguatime}</span>
          </div>
        )}

        {/* 发布者信息 */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <span className="text-xs text-gray-600 flex-1 truncate">{item.publisher_name}</span>
          <span className="text-[11px] text-gray-300">{formatTime(item.publish_time)}</span>
        </div>

        {/* 互动数据 */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
          <button 
            onClick={handleLike}
            disabled={isLoading}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors active:scale-95 ${
              isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />
            <span>{likesCount}</span>
          </button>
          <button 
            onClick={handleFavorite}
            disabled={isLoading}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors active:scale-95 ${
              isFavorited ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-400'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${isFavorited ? 'fill-current' : ''}`} />
            <span>{favoritesCount}</span>
          </button>
          <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-400 bg-gray-50">
            <MessageCircle className="w-3.5 h-3.5" />
            <span>{item.comments_count || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalIndex;
